import { Injectable } from '@angular/core';
import { IRosterValue } from "../shared/models/roster/roster-value";
import { IEmployeeRosterUpdateModel } from "../shared/models/roster/employee-roster-update-model";
import { IApiResult } from '../shared/models/api-result';
import { addDays, differenceInDays, differenceInHours, format } from 'date-fns';
import { PpsUtilityService } from './pps-utility.service';
import { TicketStatus } from '../shared/models/roster/ticket-status';

@Injectable({
  providedIn: 'root'
})
export class RosterPatternParser {
  private _seperator = " ";
  constructor(private _util: PpsUtilityService) { }

  getTokens(pattern: string): string[]{
    pattern = pattern || "";

    return pattern.toUpperCase()
      .split(this._seperator)
      .filter(e=>e)
      .map(s => {
        if (s === "DS"){
          s = "DAY";
        }else if (s === "NS"){
          s = "NIGHT";
        }

        return s;
      });
  }

  parse(employeeId: number, from: Date, pattern: string, rosterCodes): IApiResult<IEmployeeRosterUpdateModel[]>{
    let parsingResult: IApiResult<IEmployeeRosterUpdateModel[]> = {succeed: false, result:[], errors: []};
    let tokenList = this.getTokens(pattern);

    let dayCounter = 0;
    for (let i = 0; i < tokenList.length; i ++){
      let token = tokenList[i];
      let rosterValue: IRosterValue = {
        rc:{
          id: 0,
          code: token
        }, 
        sn: null, 
        ts:{
          id: 0, 
          description: null
        }
      };
      
      if (!rosterCodes.hasOwnProperty(token)){
        parsingResult.errors = [`Invalid roster code: ${token}`];
        return parsingResult;   
      }

      rosterValue.rc.id = rosterCodes[token];
      
      // Set default ticket status to 3: Plane ticket planned
      if (this.shouldSetTicketStatus(token)){
        rosterValue.ts.id = TicketStatus.Planned;
      }

      // Check the last token and push the data into result
      if (i === tokenList.length - 1){
        if (this.isDayOrNightShift(token)){
          rosterValue.sn = 1;
        }

        parsingResult.result.push({
          employeeId: employeeId,
          from: addDays(from, dayCounter),
          to: addDays(from, dayCounter),
          rosterValue: rosterValue
        });

        parsingResult.succeed = true;
        return parsingResult;
      }

      let nextToken = tokenList[i+1];

      if (this.isDayOrNightShift(token)){
        let subtokens = nextToken.split(":");
        if (subtokens.length !== 2){
          parsingResult.errors = [`
            For day/night shift, provide start day# followed by total work days in the format of [DAY#:DAYS]. 
            For example: 2:6 will generate sequence 2, 3, 4, 5, 6, 7. 
            Error value: ${token}${this._seperator}${nextToken}
          `];
          return parsingResult;   
        }

        let seqStart = subtokens[0];
        let countOfDays = subtokens[1];

        if (!this.isNumeric(seqStart)){
          parsingResult.errors = [`
            For day/night shift, provide start day# followed by total work days in the format of [DAY#:DAYS].
            The DAY# must be a numeric value.  Error value:  ${seqStart} in ${token}${this._seperator}${nextToken}
          `];
          return parsingResult;   
        }

        if (!this.isNumeric(countOfDays)){
          parsingResult.errors = [`
            For day/night shift, provide start day# followed by total work days in the format of [DAY#:DAYS].
            The DAYS must be a numeric value.  Error value:  ${countOfDays} in ${token}${this._seperator}${nextToken}
          `]
          return parsingResult;   
        }

        let copyOfRosterValue = Object.assign({}, rosterValue);
        copyOfRosterValue.sn = +seqStart;
        parsingResult.result.push({
          employeeId: employeeId,
          from: addDays(from, dayCounter),
          to: addDays(from, dayCounter + (+countOfDays) - 1),
          rosterValue: copyOfRosterValue
        });

        dayCounter += (+countOfDays);

        i ++;
      }else{
        // Check if next token is a number or another roster code
        if (this.isNumeric(nextToken) || rosterCodes.hasOwnProperty(nextToken)){
          let n = this.isNumeric(nextToken) ? +nextToken : 1;

          let rv = Object.assign({}, rosterValue);
          parsingResult.result.push({
            employeeId: employeeId,
            from: addDays(from, dayCounter),
            to: addDays(from, dayCounter + n - 1),
            rosterValue: rv
          });
          dayCounter += n;

          if (this.isNumeric(nextToken)){
            i ++;
          }
        }
      }
    }

    parsingResult.succeed = true;
    return parsingResult;
  }

  getRosterValues(empRosterList: IEmployeeRosterUpdateModel[]): IRosterValue[]{
    let result: IRosterValue[] = [];

    empRosterList.forEach(empRoster => {
      var numberOfDays = this._util.daysDiff(empRoster.from, empRoster.to)  + 1;

      var assignSequence = empRoster.rosterValue.rc.code === "DAY" ||
        empRoster.rosterValue.rc.code === "NIGHT";
      var startSeq = empRoster.rosterValue.sn;

      for (let i = 0; i < numberOfDays; i ++){
        var rosterValue = Object.assign({}, empRoster.rosterValue);
        rosterValue.sn = assignSequence ? i + startSeq : null;
        result.push(rosterValue);
      }
    });

    return result;
  }

  setPayRollRosterCode(rosterValues: IRosterValue[], rosterCodes){
    for (let i = 0; i < rosterValues.length; i ++){
      let prev = i === 0 ? rosterValues[rosterValues.length - 1] : rosterValues[i - 1];
      let current = rosterValues[i];
      let next = i < rosterValues.length - 1 ? rosterValues[i + 1] : null;

      if(current.rc.code === "FIP" || current.rc.code === "DIP"){
        // FipNightStrategy
        if (next && next.rc.code === "NIGHT"){
          current.prc = {id: rosterCodes[next.rc.code], code: next.rc.code};
        }
        // FipDayStrategy
        if (next && next.rc.code === "DAY"){
          current.prc = Object.assign({}, prev ? prev.rc : current.rc);
        }
      }
      else if(current.rc.code === "FOP" || current.rc.code === "DOP"){
        // SdFopStrategy
        if (prev && prev.rc.code === "SD"){
          current.prc = {id: rosterCodes["DAY"], code: "DAY"};
        }

        // DayFopStrategy
        if (prev && prev.rc.code === "DAY"){
          current.prc = {id: rosterCodes[prev.rc.code], code: prev.rc.code};
        }
        
        // NightFopStrategy
        if (prev && prev.rc.code === "NIGHT"){
          current.prc = {id: rosterCodes[next?.rc.code], code: next?.rc.code};
        }
      }
      else if(current.rc.code === "FIA" || current.rc.code === "DIA"){
        // FiaDayStrategy
        if (next && next.rc.code === "DAY"){
          current.prc = {id: rosterCodes[next.rc.code], code: next.rc.code};
        }

        // FiaNightStrategy
        if (next && next.rc.code === "NIGHT"){
          current.prc = {id: rosterCodes[next.rc.code], code: next.rc.code};
        }
      }
      else if(current.rc.code === "FOA" || current.rc.code === "DOA"){
        // SdFoaStrategy
        if (prev && prev.rc.code === "SD"){
          current.prc = {id: rosterCodes[next?.rc.code], code: next?.rc.code};
        }

        // DayFoaStrategy
        if (prev && prev.rc.code === "DAY"){
          current.prc = {id: rosterCodes[next?.rc.code], code: next?.rc.code};
        }

        // NightFoaStrategy
        if (prev && prev.rc.code === "NIGHT"){
          current.prc = {id: rosterCodes[next?.rc.code], code: next?.rc.code};
        }
      }
      else if(this.isDayOrNightShift(current.rc.code)){
        // DayNightShiftStrategy
        current.prc = Object.assign({}, current.rc);
      }
      else{
        // DefaultRosterCodeStrategy
        current.prc = Object.assign({}, current.rc);
      }
    }

    return rosterValues;
  }

  private shouldSetTicketStatus(rosterCode: string){
    return rosterCode === "FIA" || 
      rosterCode === "FIP" || 
      rosterCode === "FOA" || 
      rosterCode === "FOP" ||
      rosterCode === "DIA" || 
      rosterCode === "DIP" || 
      rosterCode === "DOA" || 
      rosterCode === "DOP";
  }

  private isDayOrNightShift(rosterCode: string){
    return rosterCode === "DAY" || rosterCode === "NIGHT";
  }

  private isNumeric(str): boolean{
    return !isNaN(Number(str));
  }
}
